import React, { useState, useMemo, useEffect } from "react";
import { BrainCircuit, ChevronDown, ChevronUp, Clock, Database } from "lucide-react";
import { Markdown } from "../../lib/markdown";
import { runGertrudisSocial } from "../../api";
import type { DataRecord, GertrudisAuditPayload } from "../../types";
import { extractTeaser, formatDateTime, parseAuditDataset, parseTimestamp } from "./socialFormat";

interface Props {
  audit?: GertrudisAuditPayload;
  clientSlug: string;
  activePlatform: string;
  comments: DataRecord[];
  view: PanelView;
  onViewChange: (view: PanelView) => void;
  onAuditGenerated: (audit: GertrudisAuditPayload) => void;
}

export type PanelView = "hidden" | "summary" | "full";

/** Transforma encabezados estándar de Gertrudis a los 3 pilares ejecutivos requeridos */
function formatExecutiveHeaders(markdownText: string): string {
  if (!markdownText) return "";

  let result = markdownText;

  // 1. "A favor" -> "Lo que funciona"
  result = result.replace(
    /(?:^|\n)(?:#{2,4}\s*|\*\*|\*|- )?(?:B\.\s*)?(?:ANÁLISIS CUALITATIVO:\s*)?(?:A\s+favor|Aspectos\s+a\s+favor)(?:\*\*)?(?::|\n)/gi,
    "\n\n### 🟢 Lo que funciona\n",
  );

  // 2. "En contra y Fricciones" -> "Lo que falla"
  result = result.replace(
    /(?:^|\n)(?:#{2,4}\s*|\*\*|\*|- )?(?:En\s+contra\s+y\s+Fricciones|En\s+contra|Fricciones\s+y\s+Reclamos|Causas\s+Raíz)(?:\*\*)?(?::|\n)/gi,
    "\n\n### 🔴 Lo que falla\n",
  );

  // 3. "Detección crítica de riesgo y valor" / "Alertas y Riesgos" -> "Dónde mirar primero"
  result = result.replace(
    /(?:^|\n)(?:#{2,4}\s*|\*\*|\*|- )?(?:C\.\s*)?(?:DETECCIÓN\s+CRÍTICA\s+DE\s+RIESGO\s+Y\s+VALOR|ALERTAS\s+Y\s+RIESGOS\s+TRANSVERSALES|Alertas\s+y\s+Riesgos|Dónde\s+mirar\s+primero)(?:\*\*)?(?::|\n)/gi,
    "\n\n### 🟡 Dónde mirar primero\n",
  );

  return result;
}

export function SocialQualitativeReport({ audit, clientSlug, activePlatform, comments, view, onViewChange, onAuditGenerated }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUsedData, setShowUsedData] = useState(false);

  const setView = onViewChange;

  const insight = audit?.insight?.trim();
  const history = audit?.history ?? [];
  const previous = history.slice(1);
  const [activeSection, setActiveSection] = useState<string>("ALL");

  const formatAuditDate = (isoString?: string | null) =>
    isoString ? formatDateTime(isoString) : "Fecha no disponible";

  const sections = useMemo(() => {
    if (!insight) return [];
    const parts = insight.split(/(?=^##\s+)/m).map((p) => p.trim()).filter(Boolean);
    return parts.map((part) => {
      const match = part.match(/^##\s+([^\n\r]+)/);
      const title = match ? match[1].replace(/^[0-9.]+\s*/, "").trim() : "General";
      return { title, content: part };
    });
  }, [insight]);

  // Si cambia la plataforma superior, sincronizar la sección activa
  useEffect(() => {
    if (activePlatform === "meta_ig") {
      const found = sections.find((s) => s.title.toLowerCase().includes("instagram"));
      if (found) setActiveSection(found.title);
    } else if (activePlatform === "meta_fb") {
      const found = sections.find((s) => s.title.toLowerCase().includes("facebook"));
      if (found) setActiveSection(found.title);
    } else if (activePlatform === "gbp") {
      const found = sections.find(
        (s) => s.title.toLowerCase().includes("google") || s.title.toLowerCase().includes("gbp"),
      );
      if (found) setActiveSection(found.title);
    } else if (activePlatform === "all") {
      const found = sections.find((s) => s.title.toLowerCase().includes("global"));
      if (found) setActiveSection(found.title);
      else setActiveSection("ALL");
    }
  }, [activePlatform, sections]);

  const displayedContent = useMemo(() => {
    if (!insight) return "";
    let base = insight;
    if (activeSection !== "ALL") {
      const found = sections.find((s) => s.title === activeSection);
      if (found) base = found.content;
    }
    return formatExecutiveHeaders(base);
  }, [insight, activeSection, sections]);

  const teaser = useMemo(() => extractTeaser(displayedContent), [displayedContent]);
  const datasetInfo = useMemo(() => parseAuditDataset(audit?.dataset), [audit?.dataset]);

  // Comentarios de terceros posteriores a la auditoría: indica si el reporte quedó desfasado.
  const newSinceAudit = useMemo(() => {
    const auditTime = audit?.timestamp ? parseTimestamp(audit.timestamp) : null;
    if (!auditTime) return null;
    let n = 0;
    for (const c of comments) {
      if (c.is_owner_author || c.is_reply) continue;
      const created = c.created_time ? parseTimestamp(String(c.created_time)) : null;
      if (created && created > auditTime) n += 1;
    }
    return n;
  }, [audit?.timestamp, comments]);

  const runAudit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await runGertrudisSocial(clientSlug);
      onAuditGenerated(result);
      setView("full");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No fue posible invocar a Gertrudis Social.");
    } finally {
      setBusy(false);
    }
  };

  const fresh = Boolean(insight) && !audit?.id;
  const stale = newSinceAudit !== null && newSinceAudit > 0;

  const iconButton: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    padding: "5px 10px",
  };

  return (
    <div
      className="social-report-panel"
      style={{
        background: "linear-gradient(135deg, rgba(125, 150, 255, 0.10), rgba(125, 150, 255, 0.02) 60%), var(--surface, #11141b)",
        border: "1px solid rgba(125, 150, 255, 0.28)",
        borderRadius: "14px",
        padding: view === "hidden" ? "12px 20px" : "18px 22px",
        marginBottom: "20px",
      }}
    >
      {/* Barra de acción IA: siempre visible */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
          <BrainCircuit size={20} color="var(--blue, #7d96ff)" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>Gertrudis · Auditoría de reputación</div>
            {insight && view !== "hidden" ? (
              <div style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginTop: "2px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={11} /> {formatAuditDate(audit?.timestamp)}
                  {fresh ? " · recién generada" : ""}
                </span>
                {newSinceAudit !== null && (
                  <span
                    style={{
                      color: stale ? "#f5a524" : "var(--green, #4bd4a0)",
                      fontWeight: 650,
                    }}
                  >
                    {stale
                      ? `${newSinceAudit} ${newSinceAudit === 1 ? "comentario nuevo" : "comentarios nuevos"} desde esta auditoría`
                      : "Al día con los comentarios actuales"}
                  </span>
                )}
              </div>
            ) : !insight ? (
              <div style={{ fontSize: "11px", color: "var(--muted, #8e96a8)", marginTop: "2px" }}>
                Causas raíz, atributos valorados y alertas críticas a partir de los comentarios.
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          {insight && view !== "hidden" && (
            <button
              type="button"
              className="button button-ghost button-sm"
              style={iconButton}
              onClick={() => setView(view === "full" ? "summary" : "full")}
            >
              {view === "full" ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {view === "full" ? "Colapsar" : "Ver reporte completo"}
            </button>
          )}
          <button
            className="button button-primary button-sm"
            type="button"
            disabled={busy}
            onClick={runAudit}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            {busy ? (
              <>
                <span className="button-spinner" /> Gertrudis está analizando…
              </>
            ) : (
              <>
                <BrainCircuit size={15} /> {insight ? "Actualizar auditoría" : "Auditar con Gertrudis"}
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="gertrudis-error" style={{ marginTop: "12px" }}>
          {error}
        </div>
      )}

      {/* Resumen: 3 ideas, sin tablas ni detalle */}
      {insight && view === "summary" && teaser.length > 0 && (
        <ul style={{ margin: "14px 0 0 0", padding: "0 0 0 18px", display: "grid", gap: "6px", fontSize: "13px", lineHeight: 1.5, color: "var(--text, #f6f7fb)" }}>
          {teaser.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}

      {/* Reporte completo */}
      {insight && view === "full" && (
        <div style={{ marginTop: "16px" }}>
          {sections.length > 1 && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: "14px",
                padding: "4px",
                background: "var(--surface-2, #151923)",
                borderRadius: "8px",
                width: "fit-content",
              }}
            >
              <button
                type="button"
                className={`button button-sm ${activeSection === "ALL" ? "button-primary" : "button-ghost"}`}
                style={{ fontSize: "11px", padding: "4px 10px" }}
                onClick={() => setActiveSection("ALL")}
              >
                Todo el reporte
              </button>
              {sections.map((sec) => (
                <button
                  key={sec.title}
                  type="button"
                  className={`button button-sm ${activeSection === sec.title ? "button-primary" : "button-ghost"}`}
                  style={{ fontSize: "11px", padding: "4px 10px" }}
                  onClick={() => setActiveSection(sec.title)}
                >
                  {sec.title}
                </button>
              ))}
            </div>
          )}

          <div
            className="gertrudis-response-copy"
            style={{
              lineHeight: "1.6",
              fontSize: "13px",
              background: "rgba(0, 0, 0, 0.15)",
              padding: "16px 20px",
              borderRadius: "10px",
              border: "1px solid var(--line, rgba(255, 255, 255, 0.05))",
            }}
          >
            <Markdown source={displayedContent} />
          </div>

          {/* Datos que usó Gertrudis */}
          <div style={{ marginTop: "14px" }}>
            <button
              type="button"
              className="button button-ghost button-sm"
              style={iconButton}
              onClick={() => setShowUsedData((v) => !v)}
            >
              <Database size={14} />
              {showUsedData ? "Ocultar datos que usó Gertrudis" : "Ver datos que usó Gertrudis"}
            </button>
            {showUsedData && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "14px 16px",
                  background: "rgba(0, 0, 0, 0.15)",
                  border: "1px solid var(--line, rgba(255, 255, 255, 0.05))",
                  borderRadius: "10px",
                  fontSize: "12px",
                  color: "var(--muted, #8e96a8)",
                }}
              >
                {datasetInfo ? (
                  <div style={{ display: "grid", gap: "10px" }}>
                    <div style={{ display: "flex", gap: "18px", flexWrap: "wrap" }}>
                      {datasetInfo.total !== null && (
                        <span>
                          Comentarios analizados: <strong style={{ color: "var(--text, #f6f7fb)" }}>{datasetInfo.total}</strong>
                        </span>
                      )}
                      {datasetInfo.rango && (
                        <span>
                          Rango: <strong style={{ color: "var(--text, #f6f7fb)" }}>{datasetInfo.rango}</strong>
                        </span>
                      )}
                      {datasetInfo.objetivo && (
                        <span>
                          Objetivo del cliente: <strong style={{ color: "var(--text, #f6f7fb)" }}>{datasetInfo.objetivo}</strong>
                        </span>
                      )}
                    </div>
                    {datasetInfo.hitos && (
                      <div style={{ padding: "8px 12px", background: "rgba(56, 189, 248, 0.08)", borderRadius: "6px", border: "1px solid rgba(56, 189, 248, 0.2)", fontSize: "11px", color: "#e2e8f0" }}>
                        <strong style={{ color: "#38bdf8", display: "block", marginBottom: "4px" }}>🚩 Contexto de hitos considerados:</strong>
                        <div style={{ whiteSpace: "pre-line" }}>{datasetInfo.hitos}</div>
                      </div>
                    )}
                    {datasetInfo.tablas && <Markdown source={datasetInfo.tablas} />}
                  </div>
                ) : (
                  <span>Esta auditoría no guardó el detalle de los datos usados.</span>
                )}
              </div>
            )}
          </div>

          {/* Historial colapsable de auditorías */}
          {previous.length > 0 && (
            <details className="audit-history-disclosure" style={{ marginTop: "14px" }}>
              <summary style={{ fontSize: "12px", cursor: "pointer", color: "var(--muted, #8e96a8)" }}>
                Ver auditorías anteriores <span>({previous.length})</span>
              </summary>
              <div className="audit-history-list" style={{ marginTop: "10px" }}>
                {previous.map((item, index) => (
                  <details
                    key={item.id ?? `${item.timestamp}-${index}`}
                    style={{
                      marginBottom: "8px",
                      background: "var(--surface-2, #151923)",
                      borderRadius: "8px",
                      padding: "8px 12px",
                    }}
                  >
                    <summary style={{ cursor: "pointer", fontSize: "12px", display: "flex", justifyContent: "space-between" }}>
                      <span>Auditoría {previous.length - index}</span>
                      <time style={{ color: "var(--muted, #8e96a8)" }}>{formatAuditDate(item.timestamp)}</time>
                    </summary>
                    <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid var(--line)" }}>
                      <Markdown source={formatExecutiveHeaders(item.insight ?? "")} />
                    </div>
                  </details>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
